/**
 * Worthly2Welcome - w2_05. The first screens a new person sees, in the
 * Worthly 2.0 look:
 *   - W2LoginPage: the welcome screen (same cards and words as before), then
 *     "Let's get you signed up!" with Apple and Google.
 *   - W2NameEntryPage: "What should we call you?" - name only.
 * w2_07: the original fanned cards on a pastel glow, and a fuller name page.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { W2, W2Page, W2Bubble, W2Feat, w2Title, w2Sub, w2Btn, w2WhiteBtn, w2Field, w2Card } from "./w2ui";

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.182 15.09c-.87.87-1.83.73-2.75.32-.97-.42-1.86-.44-2.88 0-1.27.56-1.94.4-2.7-.32C2.18 11.35 2.83 5.96 6.88 5.73c1.18.06 2 .69 2.69.73.97-.2 1.9-.76 2.94-.69 1.24.1 2.18.59 2.79 1.48-2.56 1.53-1.95 4.89.58 5.82-.47 1.21-.68 1.73-1.28 2.82h-.38ZM9.47 5.68C9.32 3.73 10.89 2.12 12.72 2c.27 2.22-2.02 3.9-3.25 3.68Z" fill="white" />
  </svg>
);
const GoogleLogo = () => (
  <svg width="19" height="19" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// The status bar matches the lavender page while these screens are up.
const usePageColour = () => {
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const was = meta ? meta.getAttribute('content') : null;
    if (meta) meta.setAttribute('content', W2.page);
    return () => { if (meta && was) meta.setAttribute('content', was); };
  }, []);
};

export const W2LoginPage = ({ login, handleAppleSignIn }) => {
  const [showSignIn, setShowSignIn] = useState(false);
  usePageColour();

  if (showSignIn) {
    return (
      <W2Page
        testId="w2-sign-in"
        foot={
          <p style={{ textAlign: 'center', fontSize: 12.5, color: W2.soft, lineHeight: 1.5, margin: 0 }}>
            By continuing you agree to our <a href="/terms" style={{ color: W2.soft, textDecoration: 'underline' }}>Terms</a> and <a href="/privacy" style={{ color: W2.soft, textDecoration: 'underline' }}>Privacy Policy</a>
          </p>
        }
      >
        <button
          type="button"
          onClick={() => setShowSignIn(false)}
          aria-label="Back"
          className="flex items-center justify-center"
          style={{ width: 40, height: 40, borderRadius: 999, border: 0, background: '#FFFFFF', color: W2.ink, fontSize: 20, padding: 0, cursor: 'pointer' }}
        >
          {'‹'}
        </button>
        <h1 style={{ ...w2Title, marginTop: 26 }}>Let's get you signed up!{/* w2_08: one line */}</h1>
        <p style={w2Sub}>Your money stays private to you. We never connect to your bank.</p>
        <div style={{ marginTop: 28 }}>
          <button type="button" onClick={handleAppleSignIn} style={w2Btn} data-testid="apple-login-btn">
            <AppleLogo /> Continue with Apple
          </button>
          <button type="button" onClick={login} style={{ ...w2WhiteBtn, marginTop: 10 }} data-testid="google-login-btn">
            <GoogleLogo /> Continue with Google
          </button>
        </div>
        <div style={{ background: W2.lil, color: '#4E4366', borderRadius: 18, padding: '12px 14px', fontSize: 13.5, lineHeight: 1.5, marginTop: 20 }}>
          {'\u{1F512}'} You'll only ever enter nicknames and balances, never card numbers or passwords.
        </div>
      </W2Page>
    );
  }

  // w2_07: your original fanned cards on a soft pastel glow, with the old
  // headline font and the feature chips.
  const blob = (extra) => ({ position: 'absolute', borderRadius: 999, filter: 'blur(40px)', ...extra });
  const oldCard = (extra) => ({ position: 'absolute', width: 155, height: 180, borderRadius: 20, padding: 14, boxShadow: '0 16px 32px -10px rgba(0,0,0,0.15)', ...extra });
  const cap = { fontSize: 9, fontWeight: 600, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.1em' };
  const serif = "'DM Serif Display', serif";
  const calDays = ['spend', 'win', 'win', 'spend', 'spend', 'neutral', 'win', 'win', 'spend', 'win', 'win', 'spend', 'neutral', 'win', 'win', 'spend', 'win', 'spend', 'spend', 'win', 'win'];
  return (
    <W2Page
      testId="w2-welcome"
      bg="#F6F2FB"
      bodyStyle={{ paddingTop: 60 }}
      backdrop={
        <>
          <span style={blob({ width: 280, height: 280, left: -90, top: -70, background: '#F7D3E3' })} />
          <span style={blob({ width: 260, height: 260, right: -100, top: 130, background: '#D4E3F8' })} />
          <span style={blob({ width: 240, height: 240, left: 20, top: 360, background: '#E3D9FA' })} />
          <span style={blob({ width: 220, height: 220, right: -50, bottom: 110, background: '#D6F0E2', opacity: 0.8 })} />
        </>
      }
      foot={
        <>
          <button type="button" onClick={() => setShowSignIn(true)} style={w2Btn} data-testid="get-started-btn">Get started</button>
          <div className="flex items-center justify-center" style={{ gap: 8, marginTop: 12 }}>
            <a href="/privacy" style={{ fontSize: 12.5, color: W2.soft, textDecoration: 'underline' }}>Privacy</a>
            <span style={{ fontSize: 12.5, color: W2.faint }}>{'·'}</span>
            <a href="/terms" style={{ fontSize: 12.5, color: W2.soft, textDecoration: 'underline' }}>Terms</a>
          </div>
        </>
      }
    >
      <div className="flex justify-center" style={{ height: 230 }} aria-hidden="true">
        <div style={{ position: 'relative', width: 320, height: 210 }}>
          <div style={oldCard({ top: 24, left: 28, background: 'linear-gradient(135deg, #ffffff 0%, #fdf2f8 100%)', transform: 'rotate(-8deg)', zIndex: 1 })}>
            <div style={cap}>Net Worth</div>
            <div style={{ fontFamily: serif, fontSize: 22, color: '#1a1a1a', marginTop: 4, lineHeight: 1 }}>$33,420</div>
            <div style={{ fontSize: 9, color: '#8a8a8a', marginTop: 3 }}>+$1,240 this month</div>
            <svg viewBox="0 0 140 50" preserveAspectRatio="none" style={{ width: '100%', height: 46, marginTop: 12 }}>
              <defs>
                <linearGradient id="w2ChartGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e879a0" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#e879a0" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,40 L20,35 L40,32 L60,28 L80,22 L100,18 L120,12 L140,8 L140,50 L0,50 Z" fill="url(#w2ChartGrad)" />
              <path d="M0,40 L20,35 L40,32 L60,28 L80,22 L100,18 L120,12 L140,8" stroke="#e879a0" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <div style={oldCard({ top: 0, left: 74, background: 'linear-gradient(135deg, #ffffff 0%, #f5f3ff 100%)', transform: 'rotate(2deg)', zIndex: 2 })}>
            <div style={cap}>Sinking Fund</div>
            <div style={{ fontSize: 22, marginTop: 4, lineHeight: 1 }}>{'✈️'}</div>
            <div style={{ fontSize: 10, color: '#8a8a8a', marginTop: 4 }}>Trip to Portugal</div>
            <div className="flex justify-between" style={{ fontSize: 10, color: '#4a4a4a', marginTop: 10, fontWeight: 500 }}><span>$3,400 / $5,000</span><span>68%</span></div>
            <div style={{ height: 5, background: 'rgba(233, 30, 140, 0.12)', borderRadius: 3, marginTop: 5, overflow: 'hidden' }}><div style={{ height: '100%', width: '68%', background: '#e879a0', borderRadius: 3 }} /></div>
            <div className="flex justify-between" style={{ fontSize: 10, color: '#4a4a4a', marginTop: 10, fontWeight: 500 }}><span>Emergency Fund</span><span>55%</span></div>
            <div style={{ height: 5, background: 'rgba(233, 30, 140, 0.12)', borderRadius: 3, marginTop: 5, overflow: 'hidden' }}><div style={{ height: '100%', width: '55%', background: '#e879a0', borderRadius: 3 }} /></div>
          </div>
          <div style={oldCard({ top: 32, left: 130, background: 'linear-gradient(135deg, #ffffff 0%, #ecfeff 100%)', transform: 'rotate(9deg)', zIndex: 3, boxShadow: '0 16px 32px -10px rgba(0,0,0,0.18)' })}>
            <div style={cap}>April {'·'} Calendar</div>
            <div style={{ fontFamily: serif, fontSize: 18, color: '#1a1a1a', marginTop: 6, lineHeight: 1 }}>8 wins {'\u{1F3C6}'}</div>
            <div style={{ fontSize: 10, color: '#8a8a8a', marginTop: 3 }}>This month</div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginTop: 10 }}>
              {calDays.map((d, i) => (
                <div key={i} style={{ aspectRatio: '1', borderRadius: 3, background: d === 'win' ? '#e879a0' : d === 'neutral' ? '#FDD835' : '#e5e5e5' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <h1 style={{ margin: '22px 0 0', fontFamily: serif, fontWeight: 400, fontSize: 44, lineHeight: 1.04, letterSpacing: '-0.01em', color: '#1a1a1a' }}>Clear money.<br />Calm mind.</h1>
      <p style={{ ...w2Sub, fontSize: 14.5, color: '#7E798A', marginTop: 12 }}>Worthly helps you take control of your money with smart budgets, spending rules, sinking funds, net worth tracking and more, all in one place!</p>
      <div className="flex flex-wrap" style={{ gap: 7, marginTop: 14 }}>
        {['\u{1F1E8}\u{1F1E6} Made in Canada', '\u{1F30D} 22 currencies', '✨ No ads', '\u{1F4B3} Debit and credit', '\u{1F4C8} Live net worth', '\u{1F399}\u{FE0F} Log with Siri'].map((t) => (
          <span key={t} style={{ fontSize: 12, fontWeight: 500, padding: '7px 11px', borderRadius: 999, background: 'rgba(255,255,255,0.75)', color: '#4E4A58' }}>{t}</span>
        ))}
      </div>
    </W2Page>
  );
};

export const W2NameEntryPage = ({ onSaveName, user }) => {
  const isAppleUser = user?.auth_provider === 'apple';
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  usePageColour();
  useEffect(() => {
    if (isAppleUser && user?.name) setName(user.name);
  }, [isAppleUser, user?.name]);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    await onSaveName(name.trim(), null);
    setSaving(false);
  };

  return (
    <W2Page
      testId="w2-name"
      foot={
        <button type="button" onClick={submit} disabled={!name.trim() || saving} style={{ ...w2Btn, opacity: name.trim() ? 1 : 0.4 }} data-testid="save-name-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
        </button>
      }
    >
      <W2Bubble bg={W2.mint} e={'\u{1F49C}'} size={60} />
      <h1 style={{ ...w2Title, marginTop: 20 }}>What should we<br />call you?</h1>
      <p style={w2Sub}>We'll use it to say hi every time you open Worthly!</p>
      <input
        type="text"
        value={name}
        placeholder="First name"
        readOnly={isAppleUser}
        onChange={isAppleUser ? undefined : (e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        style={{ ...w2Field, marginTop: 24, color: isAppleUser ? W2.soft : W2.ink }}
        data-testid="name-input"
      />
      {isAppleUser && (
        <p style={{ fontSize: 12.5, color: W2.soft, margin: '8px 4px 0', lineHeight: 1.45 }}>Your name comes from Sign in with Apple, so it can't be changed here.</p>
      )}
      <div style={{ ...w2Card, marginTop: 28 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: W2.faint, letterSpacing: '0.05em' }}>WHAT'S NEXT</span>
        <div style={{ marginTop: 4 }}>
          <W2Feat first e={'\u{1F3F7}\u{FE0F}'} bg={W2.lil} title="A quick setup" text="Your currency, budget, cards and savings" />
          <W2Feat e={'\u{1F5FA}\u{FE0F}'} bg={W2.mint} title="A short tour" text="What each tab can do for you" />
        </div>
      </div>
    </W2Page>
  );
};
